import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/content";
import RatingBadge from "@/components/base/RatingBadge";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = movies.length;

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  const go = (next: number) => setIndex((next + count) % count);

  if (count === 0) return null;

  return (
    <section className="relative h-[440px] md:h-[600px] w-full overflow-hidden rounded-lg">
      {movies.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title}剧照`}
            title={`${movie.title} - 激情视频`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/55 to-foreground-950/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent" />
        </div>
      ))}

      <div
        className="relative z-10 flex h-full w-full flex-col justify-center px-6 md:px-14"
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const delta = e.changedTouches[0].clientX - touchStartX.current;
          if (Math.abs(delta) > 50) go(delta < 0 ? index + 1 : index - 1);
          touchStartX.current = null;
        }}
      >
        <div key={movies[index].id} className="max-w-xl animate-fade-up">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
              {movies[index].type}
            </span>
            {movies[index].tags.map((tag) => (
              <span
                key={tag}
                className="rounded-md border border-background-50/30 px-2.5 py-1 text-xs font-medium text-background-100"
              >
                {tag}
              </span>
            ))}
          </div>
          <h1 className="text-3xl font-black leading-tight text-background-50 md:text-5xl">
            {movies[index].title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-200">
            <RatingBadge value={movies[index].rating} />
            <span>{movies[index].year}</span>
            <span className="text-background-400">·</span>
            <span>{movies[index].region}</span>
            <span className="text-background-400">·</span>
            <span>{movies[index].genres.join(" / ")}</span>
          </div>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-background-200 md:text-base line-clamp-3">
            {movies[index].synopsis}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/detail/${movies[index].id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <Link
              to={`/category?type=${encodeURIComponent(movies[index].type)}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/20 cursor-pointer"
            >
              同类推荐
            </Link>
          </div>
        </div>

        <div className="absolute bottom-6 left-6 flex items-center gap-3 md:left-14">
          {movies.map((movie, i) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到 ${movie.title}`}
              onClick={() => go(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-primary-500" : "w-4 bg-background-50/50"
              }`}
            />
          ))}
        </div>

        <div className="absolute bottom-6 right-6 hidden gap-2 md:flex md:right-14">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(index - 1)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/20 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(index + 1)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/20 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}